import type { CollectiblePlugin, ContentKind } from './content-plugin';
import { pickModuleExports, keyBy } from './registry';

/**
 * The CONTENT-PLUGIN registry (ADR-0114) — AUTO-DISCOVERED, mirroring how `./plugins` discovers hero skins.
 * Each collectible family ships a `*.plugin.{ts,tsx}` under `content/` (pets, backgrounds, the hero adapter);
 * this module GLOBS them (Vite `import.meta.glob`), so there is NO hand-maintained list: adding a collectible
 * kind is dropping its plugin module + content dir. Web-only (the `@kidshome/hero-content/art` alias runs
 * through Vite). The `**` matches both the family-level modules (`content/pets/pet.plugin.tsx`) and the hero
 * adapter (`content/heroes/hero.plugin.tsx`, a level deeper) — it must NOT match per-hero `skin.tsx` (a
 * different name) nor `_core`/`themes` (no `*.plugin.*` there).
 */
const pluginModules = import.meta.glob('../content/**/*.plugin.{ts,tsx}', {
  eager: true,
}) as Record<string, Record<string, unknown>>;

/** The CollectiblePlugin shape a `*.plugin` module exports — the value carrying `kind` + `assetFor` + `renderTile`. */
const isCollectiblePlugin = (v: unknown): v is CollectiblePlugin =>
  !!v && typeof v === 'object' && 'kind' in v && 'assetFor' in v && 'renderTile' in v;

/** kind → its plugin, built once at module load (the registry-consistency test gates that all kinds resolve). */
export const CONTENT_PLUGINS: Readonly<Record<ContentKind, CollectiblePlugin>> = keyBy(
  pickModuleExports(pluginModules, isCollectiblePlugin),
  (p) => p.kind,
) as Record<ContentKind, CollectiblePlugin>;

/** Resolve a collectible family's plugin (hero · pet · background) — throws if its `*.plugin` module is missing. */
export function contentPlugin(kind: ContentKind): CollectiblePlugin {
  const plugin = CONTENT_PLUGINS[kind];
  if (!plugin) {
    throw new Error(
      `no content plugin registered for kind "${kind}" — add a content/**/*.plugin module exporting a CollectiblePlugin`,
    );
  }
  return plugin;
}

/** Primary art URL for a `(kind, id)` pair, or undefined for "no art" — the cross-family resolver. */
export function contentAssetFor(kind: ContentKind, id: string): string | undefined {
  return CONTENT_PLUGINS[kind]?.assetFor(id);
}
